import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import {
  Bank,
  Call,
  Location,
  Note,
  People,
  Profile,
  Edit2,
  DocumentText,
  Teacher,
  Briefcase,
} from "iconsax-react";
import { formatDateForDisplay, formatMonthYear, getFamilyStatusLabel, type Locale } from "@/shared/utils";
import { Button, FileViewer, ImageViewer, ModalForm, Skeleton, StepsSwitcher, Toast } from "@/shared/ui";
import { CheckIcon, CloseIcon } from "@/shared/assets/icons";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import { TabsWithScroll } from "@/shared/components";
import { canManagerAct } from "@/features/hr/hiring/utils/stageOwnership";
import { isHiredApplication } from "@/features/hr/hiring/utils/applicationDisplayStatus";
import { useHiringGetApplicationDetailQuery } from "../../../api";
import { getNationalityLabel } from "../../../consts";
import { type ApplicationDetailResponse, type ApplicationStatus } from "../../../types";
import ApplicationSectionRenderer from "./ApplicationSectionRenderer";

interface Props {
  id: number;
  jobPosition: string;
  onClose: () => void;
  status: ApplicationStatus;
  has_contract: boolean;
  onApprove: (id: number) => void;
  onRevision: (id: number) => void;
  onReject: (id: number) => void;
  onCreateContract: (id: number) => void;
  onDownloadProfile?: (id: number) => void;
  isDownloading?: boolean;
  isReviewing?: boolean;
  isRejecting?: boolean;
  isCreatingContract?: boolean;
}

const SECTIONS = [
  { id: "personal_data", icon: Profile },
  { id: "contacts", icon: Call },
  { id: "addresses", icon: Location },
  { id: "id_documents", icon: Note },
  { id: "banking", icon: Bank },
  { id: "education", icon: Teacher },
  { id: "experience", icon: Briefcase },
  { id: "social_categories", icon: People },
] as const;

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp"];

function isImageFile(url: string): boolean {
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => lowerUrl.includes(ext));
}

function getPhotoUrl(data: ApplicationDetailResponse | undefined): string | null {
  if (!data?.documents) return null;
  const photoDoc = data.documents.find((doc) => doc.id === "photo_file");
  if (!photoDoc?.view_url) return null;
  return isImageFile(photoDoc.view_url) ? photoDoc.view_url : null;
}

export default function ShowApplicationModalWindow({
  id,
  jobPosition,
  onClose,
  status,
  has_contract,
  onApprove,
  onRevision,
  onReject,
  onCreateContract,
  isReviewing = false,
  isRejecting = false,
  isCreatingContract = false,
}: Props) {
  const { hasPermission } = useAuth();
  const { t, i18n } = useTranslation("HiringPage");
  const { t: tApply } = useTranslation("ApplyApplicationPage");
  const { t: tCommon } = useTranslation("Common");
  const locale = (i18n.language as Locale) || "ru";
  const { data, error, isLoading } = useHiringGetApplicationDetailQuery(id);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [activeStep, setActiveStep] = useState<string>("personal_data");

  const personalRef = useRef<HTMLDivElement>(null!);
  const contactsRef = useRef<HTMLDivElement>(null!);
  const addressesRef = useRef<HTMLDivElement>(null!);
  const documentsRef = useRef<HTMLDivElement>(null!);
  const bankingRef = useRef<HTMLDivElement>(null!);
  const educationRef = useRef<HTMLDivElement>(null!);
  const experienceRef = useRef<HTMLDivElement>(null!);
  const socialCategoriesRef = useRef<HTMLDivElement>(null!);

  const tabs = [
    { id: "personal", label: t("modals.personalData"), ref: personalRef },
    { id: "contacts", label: t("modals.contacts"), ref: contactsRef },
    { id: "addresses", label: t("modals.addresses"), ref: addressesRef },
    { id: "documents", label: t("modals.documents"), ref: documentsRef },
    { id: "banking", label: t("modals.banking"), ref: bankingRef },
    { id: "education", label: t("modals.education"), ref: educationRef },
    { id: "experience", label: t("modals.workExperience"), ref: experienceRef },
    ...(data?.social_categories && data.social_categories.length > 0
      ? [{ id: "social_categories", label: t("modals.socialCategories"), ref: socialCategoriesRef }]
      : []),
  ];

  const photoUrl = getPhotoUrl(data);
  const [imageError, setImageError] = useState(false);
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [photoUrl]);

  function renderUserCreationBanner(appData: ApplicationDetailResponse) {
    if (!appData.user_creation_failed) return null;
    return (
      <div className="mb-4">
        <Toast
          color="negative"
          isFullWidth
          closable={false}
          autoClose={false}
          text={appData.user_creation_error || t("applications.userCreationFailed")}
        />
      </div>
    );
  }

  function renderSkeleton() {
    return (
      <div className="flex flex-col h-full">
        <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke pt-2">
          <Skeleton height={28} width={180} />
        </div>

        <div className="flex items-center gap-4 py-5 border-b surface-base-stroke">
          <Skeleton circle height={56} width={56} />
          <div className="flex flex-col gap-2 flex-1">
            <Skeleton height={20} width="50%" />
            <Skeleton height={16} width="30%" />
          </div>
        </div>

        <div className="py-5 border-b surface-base-stroke">
          <Skeleton height={32} width="100%" />
        </div>

        <div className="flex-1 py-4">
          <div className="flex flex-col gap-6">
            {Array.from({ length: 10 }).map((_, index) => (
              <div key={index} className="flex flex-col gap-3">
                <Skeleton height={16} width="40%" />
                <Skeleton height={32} />
                <Skeleton height={1} className="surface-base-stroke w-full" />
              </div>
            ))}

            <div className="flex justify-end items-center gap-3 pt-6 border-t surface-base-stroke">
              <Skeleton height={40} width={120} />
              <Skeleton height={40} width={160} />
              <Skeleton height={40} width={120} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderFullScreenContent(appData: ApplicationDetailResponse) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke pt-2 pl-5">
          <h4 className="text-display-2xs content-base-primary">{t("modals.personalSheet")}</h4>
        </div>

        <div className="flex items-center gap-4 py-5 border-b surface-base-stroke">
          <div className="w-14 aspect-square rounded-full flex items-center justify-center background-brand-subtle overflow-hidden">
            {photoUrl && !imageError ? (
              <button type="button" onClick={() => setShowPhotoViewer(true)} className="w-full h-full cursor-pointer">
                <img
                  src={photoUrl}
                  alt={`${appData.personal_info.surname} ${appData.personal_info.name}`}
                  className="w-full h-full object-cover"
                  onError={() => setImageError(true)}
                />
              </button>
            ) : (
              <span className="text-black text-xl font-bold">
                {appData.personal_info.name.charAt(0)}
                {appData.personal_info.surname.charAt(0)}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <p className="text-label-lg content-base-primary">
              {appData.personal_info.surname} {appData.personal_info.name} {appData.personal_info.father_name ?? ""}
            </p>
            <span className="text-body-regular-xs content-base-secondary">{jobPosition}</span>
          </div>
        </div>

        {renderUserCreationBanner(appData)}

        <div className="flex-1 overflow-auto min-h-0 page-scroll">
          <StepsSwitcher
            steps={SECTIONS.filter((section) => {
              if (section.id === "social_categories") {
                return appData?.social_categories && appData.social_categories.length > 0;
              }
              return true;
            }).map((section) => {
              const labelMap: Record<string, string> = {
                personal_data: t("modals.personalData"),
                contacts: t("modals.contacts"),
                addresses: t("modals.addresses"),
                id_documents: t("modals.documents"),
                banking: t("modals.banking"),
                education: t("modals.education"),
                experience: t("modals.workExperience"),
                social_categories: t("modals.socialCategories"),
              };
              return {
                id: section.id,
                label: labelMap[section.id] || section.id,
                icon: section.icon,
              };
            })}
            activeStep={activeStep}
            onStepChange={setActiveStep}
            className="h-full min-h-0"
            contentClassName="flex flex-col min-h-0">
            <ApplicationSectionRenderer section={activeStep} data={appData} />
          </StepsSwitcher>
        </div>
      </div>
    );
  }

  function renderNormalContent(appData: ApplicationDetailResponse) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke pt-2">
          <h4 className="text-display-2xs content-base-primary">{t("modals.personalSheet")}</h4>
        </div>

        <div className="flex items-center gap-4 py-5 border-b surface-base-stroke">
          <div className="w-14 aspect-square rounded-full flex items-center justify-center background-brand-subtle overflow-hidden">
            {photoUrl && !imageError ? (
              <button type="button" onClick={() => setShowPhotoViewer(true)} className="w-full h-full cursor-pointer">
                <img
                  src={photoUrl}
                  alt={`${appData.personal_info.surname} ${appData.personal_info.name}`}
                  className="w-full h-full object-cover"
                  onError={() => setImageError(true)}
                />
              </button>
            ) : (
              <span className="content-base-primary text-xl font-bold">
                {appData.personal_info.name.charAt(0)}
                {appData.personal_info.surname.charAt(0)}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <p className="text-label-lg content-base-primary">
              {appData.personal_info.surname} {appData.personal_info.name} {appData.personal_info.father_name ?? ""}
            </p>

            <span className="text-body-regular-xs content-base-secondary">{jobPosition}</span>
          </div>
        </div>

        {renderUserCreationBanner(appData)}

        <div className="py-5">
          <TabsWithScroll items={tabs} />
        </div>

        <div className="flex-1 py-4 pl-0.5">
          <div className="flex flex-col gap-7">
            <div ref={personalRef} className="flex flex-col gap-5">
              <div className="pb-3 border-b surface-base-stroke">
                <h6 className="text-body-bold-lg content-base-primary">{t("modals.personalData")}</h6>
              </div>
              <div className="flex flex-col">
                {[
                  { key: "iin", value: appData.personal_info.iin },
                  { key: "dateOfBirth", value: formatDateForDisplay(appData.personal_info.date_of_birth, false) },
                  {
                    key: "gender",
                    value:
                      appData.personal_info.gender === "male"
                        ? t("modals.genderValues.male")
                        : t("modals.genderValues.female"),
                  },
                  {
                    key: "familyStatus",
                    value: getFamilyStatusLabel(appData.personal_info.family_status, appData.personal_info.gender, tCommon),
                  },
                  {
                    key: "cityOfBirth",
                    value: appData.personal_info.city_of_birth
                      ? locale === "kk"
                        ? appData.personal_info.city_of_birth.name_kk
                        : locale === "en"
                          ? appData.personal_info.city_of_birth.name_en
                          : appData.personal_info.city_of_birth.name_ru
                      : appData.personal_info.city_of_birth_other || "",
                  },
                  {
                    key: "nationality",
                    value: getNationalityLabel(
                      appData.personal_info.nationality,
                      appData.personal_info.gender,
                      tApply,
                    ),
                  },
                  { key: "citizenship", value: appData.personal_info.citizenship },
                ].map((field, index, array) => (
                  <div
                    key={field.key}
                    className={cn(
                      "py-3 flex justify-between items-start gap-3",
                      index < array.length - 1 && "border-b surface-base-stroke",
                    )}>
                    <span className="text-body-regular-md content-base-secondary">{t(`modals.${field.key}`)}</span>
                    <p className="text-body-bold-md content-base-primary text-right">{field.value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div ref={contactsRef} className="flex flex-col gap-5">
              <div className="pb-3 border-b surface-base-stroke">
                <h6 className="text-body-bold-lg content-base-primary">{t("modals.contacts")}</h6>
              </div>

              <div className="flex flex-col">
                {[
                  { key: "email", value: appData.contact_info.email },
                  { key: "phone", value: appData.contact_info.phone },
                  { key: "additionalPhone", value: appData.contact_info.phone_additional },
                ]
                  .filter((field) => field.value)
                  .map((field, index, array) => (
                    <div
                      key={field.key}
                      className={cn(
                        "py-3 flex justify-between items-start gap-3",
                        index < array.length - 1 && "border-b surface-base-stroke",
                      )}>
                      <span className="text-body-regular-md content-base-secondary">{t(`modals.${field.key}`)}</span>
                      <p className="text-body-bold-md content-base-primary text-right">{field.value}</p>
                    </div>
                  ))}
              </div>
            </div>

            <div ref={addressesRef} className="flex flex-col gap-5">
              <div className="pb-3 border-b surface-base-stroke">
                <h6 className="text-body-bold-lg content-base-primary">{t("modals.addresses")}</h6>
              </div>

              <div className="flex flex-col">
                {[
                  { key: "registrationAddress", value: appData.addresses.registration },
                  { key: "actualAddress", value: appData.addresses.factual },
                ]
                  .filter((field) => field.value)
                  .map((field, index, array) => (
                    <div
                      key={field.key}
                      className={cn(
                        "py-3 flex justify-between items-start gap-3",
                        index < array.length - 1 && "border-b surface-base-stroke",
                      )}>
                      <span className="text-body-regular-md content-base-secondary">{t(`modals.${field.key}`)}</span>
                      <p className="text-body-bold-md content-base-primary text-right">{field.value}</p>
                    </div>
                  ))}
              </div>
            </div>

            <div ref={documentsRef} className="flex flex-col gap-5">
              <div className="pb-3 border-b surface-base-stroke">
                <h6 className="text-body-bold-lg content-base-primary">{t("modals.documents")}</h6>
              </div>

              <div className="flex flex-col">
                <div className="py-3 flex justify-between items-start gap-3 border-b surface-base-stroke">
                  <span className="text-body-bold-md content-base-secondary">
                    {t(appData.personal_info.is_resident ? "modals.nationalId" : "modals.residencePermit")}
                  </span>
                </div>

                {[
                  { key: appData.personal_info.is_resident ? "idNumber" : "residencePermitNumber", value: appData.id_documents.national_id_number },
                  { key: "issuedBy", value: appData.id_documents.national_id_issued_by },
                  { key: "issueDate", value: formatDateForDisplay(appData.id_documents.national_id_issue_date, false) },
                  { key: "expiryDate", value: formatDateForDisplay(appData.id_documents.national_id_expiry_date, false) },
                ].map((field, index, array) => (
                  <div
                    key={field.key}
                    className={cn(
                      "py-3 flex justify-between items-start gap-3",
                      index < array.length - 1 && "border-b surface-base-stroke",
                    )}>
                    <span className="text-body-regular-md content-base-secondary">{t(`modals.${field.key}`)}</span>
                    <p className="text-body-bold-md content-base-primary text-right">{field.value}</p>
                  </div>
                ))}

                {appData.documents.find((doc) => doc.id === "national_id_file")?.view_url && (
                  <div className="py-3 flex flex-col gap-3">
                    <span className="text-body-regular-md content-base-secondary">
                      {tApply(`id.${appData.personal_info.is_resident ? "identity" : "residence_permit"}.file.label`)}
                    </span>
                    <FileViewer existingFileUrl={appData.documents.find((doc) => doc.id === "national_id_file")?.view_url} />
                  </div>
                )}
              </div>

              {appData.documents.find((doc) => doc.id === "military_certificate_file")?.view_url && (
                <div className="flex flex-col">
                  <div className="py-3 flex justify-between items-start gap-3 border-b surface-base-stroke">
                    <span className="text-body-bold-md content-base-secondary">{t("modals.militaryCertificate")}</span>
                  </div>

                  <div className="py-3 flex flex-col gap-3">
                    <span className="text-body-regular-md content-base-secondary">
                      {tApply("id.military.file.label")}
                    </span>
                    <FileViewer existingFileUrl={appData.documents.find((doc) => doc.id === "military_certificate_file")?.view_url} />
                  </div>
                </div>
              )}
            </div>

            <div ref={bankingRef} className="flex flex-col gap-5">
              <div className="pb-3 border-b surface-base-stroke">
                <h6 className="text-body-bold-lg content-base-primary">{t("modals.banking")}</h6>
              </div>

              <div className="flex flex-col">
                {[
                  { key: "bankName", value: appData.banking.bank_name },
                  { key: "iban", value: appData.banking.iban_account },
                  { key: "bik", value: appData.banking.bik_number },
                ].map((field, index, array) => (
                  <div
                    key={field.key}
                    className={cn(
                      "py-3 flex justify-between items-start gap-3",
                      index < array.length - 1 && "border-b surface-base-stroke",
                    )}>
                    <span className="text-body-regular-md content-base-secondary">{t(`modals.${field.key}`)}</span>
                    <p className="text-body-bold-md content-base-primary text-right">{field.value}</p>
                  </div>
                ))}

                {appData.documents.find((doc) => doc.id === "bank_certificate_file")?.view_url && (
                  <div className="py-3 flex flex-col gap-3">
                    <span className="text-body-regular-md content-base-secondary">
                      {tApply("banking.certificate.label")}
                    </span>
                    <FileViewer existingFileUrl={appData.documents.find((doc) => doc.id === "bank_certificate_file")?.view_url} />
                  </div>
                )}
              </div>
            </div>

            <div ref={educationRef} className="flex flex-col gap-5">
              <div className="pb-3 border-b surface-base-stroke">
                <h6 className="text-body-bold-lg content-base-primary">{t("modals.education")}</h6>
              </div>

              {appData.education && appData.education.length > 0 ? (
                appData.education.map((edu) => (
                  <div key={edu.id} className="flex flex-col">
                    <div className="pb-3 border-b surface-base-stroke">
                      <h6 className="text-body-bold-lg content-base-primary">
                        {edu.degree
                          ? t(`modals.degreeValues.${edu.degree}`, { defaultValue: edu.degree })
                          : t("modals.education")}
                      </h6>
                    </div>
                    {[
                      { key: "university", value: edu.university_name },
                      { key: "specialty", value: edu.specialty },
                      { key: "graduationYear", value: edu.graduation_year },
                    ]
                      .filter((field) => field.value)
                      .map((field, index, array) => (
                        <div
                          key={field.key}
                          className={cn(
                            "py-3 flex justify-between items-start gap-3",
                            index < array.length - 1 && "border-b surface-base-stroke",
                          )}>
                          <span className="text-body-regular-md content-base-secondary">
                            {t(`modals.${field.key}`)}
                          </span>
                          <p className="text-body-bold-md content-base-primary text-right">{field.value}</p>
                        </div>
                      ))}
                    {edu.diploma_file_url && (
                      <div className="py-3 flex flex-col gap-3">
                        <span className="text-body-regular-md content-base-secondary">
                          {tApply("education.diploma_file.label")}
                        </span>
                        <FileViewer existingFileUrl={edu.diploma_file_url} />
                      </div>
                    )}
                    {edu.diploma_transcript_file_url && (
                      <div className="py-3 flex flex-col gap-3">
                        <span className="text-body-regular-md content-base-secondary">
                          {tApply("education.diploma_transcript.label")}
                        </span>
                        <FileViewer existingFileUrl={edu.diploma_transcript_file_url} />
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-body-regular-md content-base-secondary py-4">{t("modals.noEducation")}</p>
              )}
            </div>

            <div ref={experienceRef} className="flex flex-col gap-5">
              <div className="pb-3 border-b surface-base-stroke">
                <h6 className="text-body-bold-lg content-base-primary">{t("modals.workExperience")}</h6>
              </div>

              {appData.documents.find((doc) => doc.id === "work_proof_file")?.view_url && (
                <div className="py-3 flex flex-col gap-3">
                  <span className="text-body-regular-md content-base-secondary">
                    {tApply("experience.proof.title")}
                  </span>
                  <FileViewer existingFileUrl={appData.documents.find((doc) => doc.id === "work_proof_file")?.view_url} />
                </div>
              )}

              {appData.work_experience && appData.work_experience.length > 0 ? (
                appData.work_experience.map((work) => (
                  <div key={work.id} className="flex flex-col">
                    <div className="pb-3 border-b surface-base-stroke">
                      <h6 className="text-body-bold-lg content-base-primary">{work.organization_name}</h6>
                    </div>
                    {[
                      { key: "speciality", value: work.speciality },
                      { key: "workPosition", value: work.work_position },
                      {
                        key: "workFormat",
                        value: work.work_format ? tApply(`experience.work_format.${work.work_format}`) : "",
                      },
                      { key: "startDate", value: formatMonthYear(work.beginning_date, locale) },
                      {
                        key: "endDate",
                        value: work.is_current
                          ? tApply("experience.working_now")
                          : formatMonthYear(work.end_date, locale),
                      },
                    ]
                      .filter((field) => field.value)
                      .map((field, index, array) => (
                        <div
                          key={field.key}
                          className={cn(
                            "py-3 flex justify-between items-start gap-3",
                            index < array.length - 1 && "border-b surface-base-stroke",
                          )}>
                          <span className="text-body-regular-md content-base-secondary">
                            {t(`modals.${field.key}`)}
                          </span>
                          <p className="text-body-bold-md content-base-primary text-right">{field.value}</p>
                        </div>
                      ))}
                  </div>
                ))
              ) : (
                <p className="text-body-regular-md content-base-secondary py-4">{t("modals.noWorkExperience")}</p>
              )}
            </div>

            {appData.social_categories && appData.social_categories.length > 0 && (
              <div ref={socialCategoriesRef} className="flex flex-col gap-5">
                <div className="pb-3 border-b surface-base-stroke">
                  <h6 className="text-body-bold-lg content-base-primary">{t("modals.socialCategories")}</h6>
                </div>
                {appData.social_categories.map((category) => {
                  return (
                    <div key={category.id} className="flex flex-col">
                      <div className="pb-3 border-b surface-base-stroke">
                        <h6 className="text-body-bold-lg content-base-primary">
                          {category.category_type_display || category.category_type}
                        </h6>
                      </div>
                      {category.issue_date && (
                        <div className="py-3 flex justify-between items-start gap-3 border-b surface-base-stroke">
                          <span className="text-body-regular-md content-base-secondary">
                            {tApply("social.issue_date.label")}
                          </span>
                          <p className="text-body-bold-md content-base-primary text-right">
                            {formatDateForDisplay(category.issue_date, false)}
                          </p>
                        </div>
                      )}
                      {category.expiry_date && (
                        <div className="py-3 flex justify-between items-start gap-3 border-b surface-base-stroke">
                          <span className="text-body-regular-md content-base-secondary">
                            {tApply("social.expiry_date.label")}
                          </span>
                          <p className="text-body-bold-md content-base-primary text-right">
                            {formatDateForDisplay(category.expiry_date, false)}
                          </p>
                        </div>
                      )}
                      {category.notes && (
                        <div className="py-3 flex justify-between items-start gap-3 border-b surface-base-stroke">
                          <span className="text-body-regular-md content-base-secondary">
                            {tApply("social.notes.label")}
                          </span>
                          <p className="text-body-bold-md content-base-primary text-right">{category.notes}</p>
                        </div>
                      )}
                      <div className="py-3 flex flex-col gap-3">
                        <span className="text-body-regular-md content-base-secondary">
                          {tApply("social.document.label")}
                        </span>
                        <FileViewer
                          existingFileUrl={appData.documents.find((doc) => doc.id === `social_${category.id}`)?.view_url}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

          </div>
        </div>
      </div>
    );
  }

  function renderFooter() {
    if (isLoading || error || !data) return undefined;

    const appData = data;
    const canAct = status === "submitted" && canManagerAct(appData.stage);
    const canManage = canAct && hasPermission(PERMISSIONS.HR.CREATE_EMPLOYEE);
    const canCreate =
      status === "approved" && !has_contract && !isHiredApplication(appData) && hasPermission(PERMISSIONS.HR.CREATE_CONTRACT);

    let buttonCount = 0;
    if (canManage) buttonCount += 3;
    if (canCreate) buttonCount++;

    if (buttonCount === 0) return undefined;

    const buttonSize = "lg" as const;

    return (
      <div className="flex flex-col gap-3">
        {canManage && (
          <Button variant="primary" size={buttonSize} className="w-full" disabled={isReviewing} onClick={() => onApprove(id)}>
            <CheckIcon />
            {isReviewing ? t("actions.approving") : t("actions.approve")}
          </Button>
        )}
        {canCreate && (
          <Button variant="primary" size={buttonSize} className="w-full" disabled={isCreatingContract} onClick={() => onCreateContract(id)}>
            <DocumentText size={16} color="currentColor" />
            {isCreatingContract ? t("actions.creating") : t("actions.createContract")}
          </Button>
        )}
        {canManage && (
          <Button variant="secondary" size={buttonSize} className="w-full" disabled={isReviewing} onClick={() => onRevision(id)}>
            <Edit2 size={16} color="currentColor" />
            {isReviewing ? t("modals.sending") : t("actions.requestRevision")}
          </Button>
        )}
        {canManage && (
          <Button variant="danger" size={buttonSize} className="w-full" disabled={isRejecting} onClick={() => onReject(id)}>
            <CloseIcon />
            {isRejecting ? t("actions.rejecting") : t("actions.reject")}
          </Button>
        )}
      </div>
    );
  }

  return (
    <>
      <ModalForm icon={Profile} onClose={onClose} resize onFullScreenChange={setIsFullScreen} footer={renderFooter()}>
        {isLoading ? (
          renderSkeleton()
        ) : error || !data ? (
          <div className="p-4">{t("modals.errorLoading")}</div>
        ) : isFullScreen ? (
          renderFullScreenContent(data)
        ) : (
          renderNormalContent(data)
        )}
      </ModalForm>

      {showPhotoViewer && photoUrl && (
        <ImageViewer imageUrl={photoUrl} onClose={() => setShowPhotoViewer(false)} />
      )}
    </>
  );
}
